import { useEffect, useState } from "react";
import {
  ConfirmDialog,
  DashboardPanel,
  FormDateField,
  FormField,
  StatCard,
} from "@NP-Webber/crm-kit";
import "@NP-Webber/crm-kit/dist/formkit/FormKit.css";
import "@NP-Webber/crm-kit/dist/tablekit/TableKit.css";
import {
  meetingsApi,
  type AssignableUser,
  type MeetingAgendaItem,
  type MeetingFull,
  type MeetingParticipant,
} from "../../../api/meetingsApi";
import { toClientApiError } from "../../../api/httpClient";
import {
  daysUntil,
  formatMeetingDateTime,
  mergeMeetingDatetime,
  splitMeetingDatetime,
} from "../constants";
import { printTeamMeeting } from "../teamMeetingPrint";
import { AgendaItemDialog } from "./AgendaItemDialog";
import { MoveAgendaItemDialog } from "./MoveAgendaItemDialog";
import { ParticipantsPanel } from "./ParticipantsPanel";

type Props = {
  meeting: MeetingFull | null;
  users: AssignableUser[];
  canManage: boolean;
  onRefresh: () => void;
  onNotify: () => void;
  accent: string;
};

export function PreparationPanel({
  meeting,
  users,
  canManage,
  onRefresh,
  onNotify,
  accent,
}: Props) {
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [editAgenda, setEditAgenda] = useState<MeetingAgendaItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MeetingAgendaItem | null>(null);
  const [moveTarget, setMoveTarget] = useState<MeetingAgendaItem | null>(null);
  const [participants, setParticipants] = useState<MeetingParticipant[]>([]);
  const [savingParts, setSavingParts] = useState(false);
  const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState("");
  const [title, setTitle] = useState("ישיבת צוות");
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  useEffect(() => {
    setParticipants(meeting?.participants || []);
    const dt = splitMeetingDatetime(meeting?.scheduledAt);
    setSchedDate(dt.date);
    setSchedTime(dt.time);
    setTitle(meeting?.title || "ישיבת צוות");
  }, [meeting]);

  if (!meeting) {
    return <div className="tm-empty">אין ישיבה פעילה — צור ישיבה חדשה מהארכיון</div>;
  }

  const agenda = meeting.agendaItems || [];
  const days = daysUntil(meeting.scheduledAt);

  const handleAddAgenda = async (payload: { title: string; notes: string | null }) => {
    try {
      await meetingsApi.addAgendaItem(meeting.id, payload);
      onRefresh();
    } catch (err) {
      throw new Error(toClientApiError(err).message || "שגיאה");
    }
  };

  const handleEditAgenda = async (payload: { title: string; notes: string | null }) => {
    if (!editAgenda) return;
    try {
      await meetingsApi.updateAgendaItem(meeting.id, editAgenda.id, payload);
      setEditAgenda(null);
      onRefresh();
    } catch (err) {
      throw new Error(toClientApiError(err).message || "שגיאה");
    }
  };

  const handleDeleteAgenda = async () => {
    if (!deleteTarget) return;
    try {
      await meetingsApi.deleteAgendaItem(meeting.id, deleteTarget.id);
      onRefresh();
    } catch {
      // snack handled by parent if needed
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleSaveSchedule = async () => {
    setSaving(true);
    try {
      const scheduledAt = mergeMeetingDatetime(schedDate, schedTime);
      await meetingsApi.update(meeting.id, {
        title,
        scheduledAt,
        status: "scheduled",
      });
      onRefresh();
    } catch (err) {
      window.alert(toClientApiError(err).message || "שגיאה");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelMeeting = async () => {
    setCancelBusy(true);
    try {
      await meetingsApi.update(meeting.id, { status: "cancelled" });
      setCancelOpen(false);
      onRefresh();
    } catch (err) {
      window.alert(toClientApiError(err).message || "שגיאה");
    } finally {
      setCancelBusy(false);
    }
  };

  const handleSaveParticipants = async () => {
    setSavingParts(true);
    try {
      await meetingsApi.updateParticipants(
        meeting.id,
        participants.map((p, i) => ({
          userId: p.userId ?? null,
          guestName: p.guestName ?? null,
          guestEmail: p.guestEmail ?? null,
          participantKind: p.participantKind,
          meetingRole: p.meetingRole,
          attended: p.attended,
          sortOrder: i + 1,
        })),
      );
      onRefresh();
    } catch (err) {
      window.alert(toClientApiError(err).message || "שגיאה");
    } finally {
      setSavingParts(false);
    }
  };

  return (
    <>
      <div className="tm-stats">
        <StatCard
          label="תאריך ישיבה"
          value={formatMeetingDateTime(meeting.scheduledAt)}
          color={accent}
        />
        <StatCard label="נושאים בסדר יום" value={String(agenda.length)} color={accent} />
        <StatCard
          label="משתתפים"
          value={String(participants.length)}
          color={accent}
        />
        <StatCard
          label="ימים לישיבה"
          value={days != null ? String(days) : "—"}
          color={accent}
        />
      </div>
      <DashboardPanel title="פרטי הישיבה">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
          }}
        >
          <FormField
            label="כותרת ישיבה"
            value={title}
            onChange={setTitle}
            disabled={!canManage}
          />
          <FormDateField
            label="תאריך"
            value={schedDate}
            onChange={setSchedDate}
            disabled={!canManage}
          />
          <FormField
            label="שעה"
            type="time"
            value={schedTime}
            onChange={setSchedTime}
            disabled={!canManage}
            hint="פורמט 24 שעות"
          />
        </div>
        {canManage ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16 }}>
            <button
              type="button"
              className="tm-btn tm-btn--primary"
              onClick={() => {
                void handleSaveSchedule();
              }}
              disabled={saving}
            >
              {saving ? "שומר…" : "קבע ישיבה"}
            </button>
            <button
              type="button"
              className="tm-btn tm-btn--outline"
              onClick={() => onNotify()}
              disabled={!meeting.scheduledAt}
            >
              שלח תזכורת עכשיו
            </button>
            <button
              type="button"
              className="tm-btn tm-btn--outline"
              onClick={() => printTeamMeeting(meeting)}
            >
              הדפסה / PDF
            </button>
            <button
              type="button"
              className="tm-btn tm-btn--outline"
              style={{ color: "var(--ck-danger)", borderColor: "var(--ck-danger)" }}
              onClick={() => setCancelOpen(true)}
              disabled={cancelBusy}
            >
              ביטול ישיבה
            </button>
            <span className="tm-chip" style={{ alignSelf: "center" }}>
              תזכורת אוטומטית: {meeting.reminderMinutesBefore || 60} דק&apos; לפני
            </span>
          </div>
        ) : (
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              className="tm-btn tm-btn--outline"
              onClick={() => printTeamMeeting(meeting)}
            >
              הדפסה / PDF
            </button>
          </div>
        )}
      </DashboardPanel>
      <ParticipantsPanel
        participants={participants}
        users={users}
        canManage={canManage}
        onChange={setParticipants}
        onSave={canManage ? () => void handleSaveParticipants() : undefined}
        saving={savingParts}
      />
      <div className="tm-panel">
        <div className="tm-panel__head">
          <h3 className="tm-panel__title">סדר יום ({agenda.length})</h3>
          <button
            type="button"
            className="tm-btn tm-btn--primary tm-btn--sm"
            onClick={() => {
              setEditAgenda(null);
              setAgendaOpen(true);
            }}
          >
            + הוסף נושא
          </button>
        </div>
        {!agenda.length ? (
          <div className="tm-empty">כל חבר צוות יכול להוסיף נושאים לדיון השבוע</div>
        ) : (
          <div className="tm-agenda-list">
            {agenda.map((item) => (
              <div key={item.id} className="tm-agenda-item">
                <div className="tm-agenda-item__body">
                  <div className="tm-agenda-item__title">{item.title}</div>
                  <div className="tm-agenda-item__meta">
                    {item.submittedByName ? `הוגש על ידי ${item.submittedByName}` : ""}
                  </div>
                  {item.notes ? (
                    <div className="tm-agenda-item__notes">{item.notes}</div>
                  ) : null}
                </div>
                {canManage ? (
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      type="button"
                      className="tm-icon-btn"
                      onClick={() => setMoveTarget(item)}
                      aria-label="העבר לישיבה אחרת"
                      title="העבר לישיבה אחרת"
                    >
                      ⇄
                    </button>
                    <button
                      type="button"
                      className="tm-icon-btn"
                      onClick={() => {
                        setEditAgenda(item);
                        setAgendaOpen(true);
                      }}
                      aria-label="ערוך"
                    >
                      ✎
                    </button>
                    <button
                      type="button"
                      className="tm-icon-btn tm-icon-btn--danger"
                      onClick={() => setDeleteTarget(item)}
                      aria-label="מחק"
                    >
                      ×
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
      <AgendaItemDialog
        open={agendaOpen}
        onClose={() => {
          setAgendaOpen(false);
          setEditAgenda(null);
        }}
        onSave={editAgenda ? handleEditAgenda : handleAddAgenda}
        initial={editAgenda}
      />
      <MoveAgendaItemDialog
        open={Boolean(moveTarget)}
        onClose={() => setMoveTarget(null)}
        item={moveTarget}
        currentMeetingId={meeting.id}
        onMoved={onRefresh}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          void handleDeleteAgenda();
        }}
        title="מחיקת נושא"
        message={`למחוק את "${deleteTarget?.title}"?`}
        confirmText="מחק"
        cancelText="ביטול"
      />
      <ConfirmDialog
        open={cancelOpen}
        onClose={() => {
          if (!cancelBusy) setCancelOpen(false);
        }}
        title="ביטול ישיבה"
        message={`לבטל את הישיבה "${meeting.title || "ישיבת צוות"}"?`}
        confirmText="ביטול ישיבה"
        cancelText="חזרה"
        loading={cancelBusy}
        onConfirm={() => {
          void handleCancelMeeting();
        }}
        onCancel={() => setCancelOpen(false)}
      />
    </>
  );
}
